import { useEffect, useState } from "react";
import { toast } from "sonner";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import { ConnectComponentsProvider, ConnectAccountOnboarding, ConnectAccountManagement, ConnectPayouts, ConnectNotificationBanner } from "@stripe/react-connect-js";
import { Landmark, ExternalLink, ShieldCheck, RefreshCw } from "lucide-react";
import { PortalShell } from "./PortalShell";
import { api } from "./api";

export default function PortalPayouts() {
  const [status, setStatus] = useState(null);
  const [connectInstance, setConnectInstance] = useState(null);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/portal/stripe/status");
      setStatus(data);
      if (data.connected) {
        const { data: session } = await api.post("/portal/stripe/account-session");
        setConnectInstance(
          loadConnectAndInitialize({
            publishableKey: session.publishable_key,
            fetchClientSecret: async () => (await api.post("/portal/stripe/account-session")).data.client_secret,
            appearance: { overlays: "dialog" },
          }),
        );
        setNeedsOnboarding(!data.details_submitted || !data.payouts_enabled);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load payout status");
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startOnboarding = async () => {
    setBusy(true);
    try {
      await api.post("/portal/stripe/connect-account");
      const { data } = await api.post("/portal/stripe/onboarding-link", { origin: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payout setup");
      setBusy(false);
    }
  };

  const ready = status?.connected && status?.details_submitted && status?.payouts_enabled;

  return (
    <PortalShell title="Payouts">
      <div className="mb-6 flex items-start gap-3 rounded-xl border border-aqua/40 bg-aqua/[0.07] p-5" data-testid="payouts-explainer">
        <ShieldCheck size={20} className="mt-0.5 shrink-0 text-aqua" />
        <p className="text-sm leading-relaxed text-offwhite">
          Payouts are powered by <strong>Stripe</strong>. Your identity, business, tax, and bank details are entered
          and stored only with Stripe — Xtreme Exotics never sees or stores them. Customer payments are always
          collected by XE first; after each Monday–Sunday week closes and XE approves your settlement, your share is
          transferred to your payout account on Wednesday.
        </p>
      </div>

      {!status ? (
        <p className="py-16 text-center text-sm text-muted">Loading…</p>
      ) : !status.connected ? (
        <div className="card-line max-w-xl rounded-2xl p-8 text-center" data-testid="payouts-setup">
          <Landmark size={26} className="mx-auto text-aqua" />
          <h2 className="mt-4 font-display text-xl font-bold text-offwhite">Connect Your Payout Account</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted">
            Set up identity verification, business information, tax details, and your payout bank account through
            Stripe's secure hosted onboarding. Takes about 5 minutes.
          </p>
          <button onClick={startOnboarding} disabled={busy} className="btn-primary mt-6 disabled:opacity-50" data-testid="connect-stripe-btn">
            {busy ? "Preparing…" : <>Set Up Payouts with Stripe <ExternalLink size={14} /></>}
          </button>
        </div>
      ) : (
        <div className="space-y-5" data-testid="payouts-connected">
          <div className="card-line flex flex-wrap items-center gap-3 rounded-xl p-5">
            <span className={`rounded-full border px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest ${ready ? "border-emerald-500/40 bg-emerald-400/10 text-emerald-700" : "border-orange-500/40 bg-orange-400/10 text-orange-700"}`} data-testid="payouts-status">
              {ready ? "Payouts active" : "Setup incomplete"}
            </span>
            {!ready && (
              <button onClick={startOnboarding} disabled={busy} className="btn-primary !px-4 !py-2 !text-xs" data-testid="continue-onboarding-btn">
                Continue Stripe Onboarding <ExternalLink size={12} />
              </button>
            )}
            <button onClick={load} className="btn-ghost ml-auto !px-4 !py-2 !text-xs" data-testid="payouts-refresh">
              <RefreshCw size={13} /> Refresh
            </button>
          </div>

          {connectInstance && (
            <ConnectComponentsProvider connectInstance={connectInstance}>
              <div className="card-line overflow-hidden rounded-xl" data-testid="stripe-notifications">
                <ConnectNotificationBanner />
              </div>
              {needsOnboarding && (
                <div className="card-line rounded-xl p-5" data-testid="stripe-onboarding-embed">
                  <p className="mb-3 font-display text-sm font-bold uppercase tracking-widest text-muted">Finish account setup</p>
                  <ConnectAccountOnboarding onExit={() => load()} />
                </div>
              )}
              <div className="card-line rounded-xl p-5" data-testid="stripe-account-management">
                <p className="mb-3 font-display text-sm font-bold uppercase tracking-widest text-muted">Business, bank & tax details</p>
                <ConnectAccountManagement />
              </div>
              <div className="card-line rounded-xl p-5" data-testid="stripe-payouts">
                <p className="mb-3 font-display text-sm font-bold uppercase tracking-widest text-muted">Payout history</p>
                <ConnectPayouts />
              </div>
            </ConnectComponentsProvider>
          )}
        </div>
      )}
    </PortalShell>
  );
}
